/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path context
 * @description Application context provider
 */
import React from "react";
import { useTranslation } from "react-i18next";
import * as config from "./../configuration/index";
import { AppContext, useAppContextHook } from "./AppContext";
import { AppLevelContext } from "./AppCtx";
import { warmModelByYearMap } from "./modelsByYear";
/**
 * This is a workaround for getting the current global context value for any function
 * without actually accessing the context.
 */
let currentGlobalContextValue: any = {};
export const getCurrentGlobalContext = (): AppLevelContext =>
  currentGlobalContextValue;
// Session storage keys for persisting period and model ID
export const SESSION_STORAGE_PERIOD_KEY = "tdf_selected_period";
export const SESSION_STORAGE_MODEL_ID_KEY = "tdf_selected_model_id";
export const SESSION_STORAGE_TIMESTAMP_KEY = "tdf_selected_timestamp";

// Session expiration time: 20 minutes in milliseconds
export const SESSION_EXPIRATION_TIME = 20 * 60 * 1000; // 20 minutes

// Helper function to check if session has expired
export const isSessionExpired = (): boolean => {
  try {
    const timestamp = sessionStorage.getItem(SESSION_STORAGE_TIMESTAMP_KEY);
    if (!timestamp) {
      return true; // No timestamp means expired
    }
    
    const storedTime = parseInt(timestamp, 10);
    const currentTime = Date.now();
    return currentTime - storedTime > SESSION_EXPIRATION_TIME;
  } catch (e) {
    return true; // Error means expired
  }
};

// Helper function to clear expired session
export const clearExpiredSession = (): void => {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_PERIOD_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_MODEL_ID_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_TIMESTAMP_KEY);
  } catch (e) {
    // Ignore errors
  }
};

// Helper functions to manage session storage with expiration
export const getStoredPeriod = (): string | null => {
  try {
    const timestamp = sessionStorage.getItem(SESSION_STORAGE_TIMESTAMP_KEY);
    if (!timestamp) {
      // No timestamp means no stored data
      return null;
    }
    
    // Check if session has expired (20 minutes)
    const storedTime = parseInt(timestamp, 10);
    const currentTime = Date.now();
    if (currentTime - storedTime > SESSION_EXPIRATION_TIME) {
      // Session expired - clear all stored values
      sessionStorage.removeItem(SESSION_STORAGE_PERIOD_KEY);
      sessionStorage.removeItem(SESSION_STORAGE_MODEL_ID_KEY);
      sessionStorage.removeItem(SESSION_STORAGE_TIMESTAMP_KEY);
      return null;
    }
    
    return sessionStorage.getItem(SESSION_STORAGE_PERIOD_KEY);
  } catch (e) {
    return null;
  }
};

export const setStoredPeriod = (period: string): void => {
  try {
    const currentTime = Date.now();
    sessionStorage.setItem(SESSION_STORAGE_PERIOD_KEY, period);
    sessionStorage.setItem(SESSION_STORAGE_TIMESTAMP_KEY, currentTime.toString());
  } catch (e) {
    console.warn("Failed to store period in sessionStorage:", e);
  }
};

export const getStoredModelId = (): string | null => {
  try {
    const timestamp = sessionStorage.getItem(SESSION_STORAGE_TIMESTAMP_KEY);
    if (!timestamp) {
      // No timestamp means no stored data
      return null;
    }
    
    // Check if session has expired (20 minutes)
    const storedTime = parseInt(timestamp, 10);
    const currentTime = Date.now();
    if (currentTime - storedTime > SESSION_EXPIRATION_TIME) {
      // Session expired - clear all stored values
      sessionStorage.removeItem(SESSION_STORAGE_PERIOD_KEY);
      sessionStorage.removeItem(SESSION_STORAGE_MODEL_ID_KEY);
      sessionStorage.removeItem(SESSION_STORAGE_TIMESTAMP_KEY);
      return null;
    }
    
    const stored = sessionStorage.getItem(SESSION_STORAGE_MODEL_ID_KEY);
    // Guard against a stale/corrupted "SC."-prefixed value written by older code:
    // fullModelId always re-adds "SC." itself, so a bare id must be stored here.
    return stored ? stored.replace(/^SC\./, "") : stored;
  } catch (e) {
    return null;
  }
};

export const setStoredModelId = (modelId: string): void => {
  try {
    const currentTime = Date.now();
    sessionStorage.setItem(SESSION_STORAGE_MODEL_ID_KEY, modelId.replace(/^SC\./, ""));
    sessionStorage.setItem(SESSION_STORAGE_TIMESTAMP_KEY, currentTime.toString());
  } catch (e) {
    console.warn("Failed to store modelId in sessionStorage:", e);
  }
};

export const AppCtxProvider: React.FC<{
  children: React.ReactNode;
}> = (props: { children: React.ReactNode }) => {
  const { i18n } = useTranslation();

  // Fetch the year -> model id map from the backend model list (instead of a static table)
  React.useEffect(() => {
    warmModelByYearMap();
  }, []);

  // Load period and modelId from session storage if available, otherwise use defaults
  const storedPeriod = getStoredPeriod();
  const storedModelId = getStoredModelId();
  const initialPeriod = storedPeriod || config.System.settings.defaultPeriod;
  const initialModelId = storedModelId || config.System.settings.defaultModel;
  
  const [ctxValue, setCtxValue] = React.useState(() => ({
    editMode: false,
    appBarVisible: true,
    language: "en",
    rtl: i18n.dir() === "rtl",
    originalPeriod: "",
    period: initialPeriod,
    modelId: initialModelId,
    fullModelId: `SC.${initialModelId}`,
    networkErrors: [],
    pageAnchors: [],
    serverURL: process.env.REACT_APP_SERVER_URL,
    sendValue: <K extends keyof AppLevelContext>(
      name: K,
      value: AppLevelContext[K]
    ) => {
      if (name === "model") {
        if (!(typeof value === "string")) {
          return;
        }
        // When model changes, also update fullModelId
        setCtxValue((oldState) => {
          return {
            ...oldState,
            modelId: value,
            fullModelId: `SC.${value}`,
          };
        });
        return;
      }
      if (name === "modelId") {
        if (!(typeof value === "string")) {
          return;
        }
        // Store modelId in session storage for persistence across page navigation
        setStoredModelId(value);
        // When modelId changes, also update fullModelId
        setCtxValue((oldState) => {
          return {
            ...oldState,
            modelId: value,
            fullModelId: `SC.${value}`,
          };
        });
        return;
      }
      if (name === "period" && typeof value === "string") {
        // Store period in session storage for persistence across page navigation
        setStoredPeriod(value);
        setCtxValue((oldState) => {
          return {
            ...oldState,
            originalPeriod: value,
            [name]: value,
          };
        });
      } else {
        setCtxValue((oldState) => {
          return {
            ...oldState,
            [name]: value,
          };
        });
      }
    },
    lastSearchUpdateTime: Date.now(),
  }));
  /**
   * Short circuit the context value to a global variable
   */
  currentGlobalContextValue = ctxValue;
  return (
    <AppContext.Provider value={ctxValue}>
      {props?.children}
    </AppContext.Provider>
  );
};
export const useAppContext = useAppContextHook;
